import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { date } from '../components/format';
import { Alert, Button, Card, Field, PasswordField, Select } from '../components/ui';
import { CONTACT_EMAIL, installGuideUrl } from '../config';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { Locale, User } from '../types';

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const { user, setUser, setLocale } = useSession();
  if (!user) return null;
  const microsoft = user.identities.find((i) => i.provider === 'microsoft');

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ProfileCard
        initial={{ first_name: user.first_name, last_name: user.last_name, locale: user.locale }}
        onSave={async (patch) => {
          const updated = await api.patchAccount(patch);
          setUser(updated);
          if (patch.locale && patch.locale !== updated.locale) await setLocale(updated.locale);
        }}
      />
      <Card title={t('account.password')}>{user.sso_only ? <SetPassword /> : <ChangePassword />}</Card>
      <Card title={t('account.microsoft')}>
        {microsoft ? (
          <div className="flex flex-col gap-1 text-[13px]">
            <div className="text-ink">{t('account.microsoftLinked', { email: microsoft.email ?? microsoft.display_name ?? '' })}</div>
            {microsoft.tenant_id && <div className="font-mono text-[11px] text-faint">{t('account.microsoftTenant', { tenant: microsoft.tenant_id })}</div>}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-[13px] text-muted">{t('account.microsoftNotLinked', { email: user.email })}</p>
            <a href={api.microsoftLinkUrl(user.email)}>
              <Button variant="secondary">{t('account.linkMicrosoft')}</Button>
            </a>
          </div>
        )}
      </Card>
      <ReviewCard user={user} />
      <Card title={t('account.excel.title')}>
        <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[13px] text-ink">
          <li>{t('account.excel.step1')}</li>
          <li>{t('account.excel.step2')}</li>
          <li>{t('account.excel.step3')}</li>
        </ol>
        <a href={installGuideUrl(i18n.language)} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[13px] text-accent underline hover:text-accent-dark">
          {t('account.excel.link')}
        </a>
      </Card>
    </div>
  );
}

/** The review of the person's conversations (sculpt-api data-policy review DEC-08): the sample they may object to by
 *  writing to us, and where they stand — their own objection, which staff record, or their organization's opt-out. */
function ReviewCard({ user }: { user: User }) {
  const { t, i18n } = useTranslation();
  const objected = user.review_sample_objection_at;
  const organization = user.organization?.review_sample_opt_out_at ?? null;
  return (
    <Card title={t('account.review.title')}>
      <div className="flex flex-col gap-2 text-[13px]">
        <p className="text-muted">
          {t('account.review.intro')}{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline hover:text-accent-dark">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        {objected !== null ? (
          <p className="text-ink" data-testid="review-status">{t('account.review.objected', { date: date(objected, i18n.language) })}</p>
        ) : organization !== null ? (
          <p className="text-ink" data-testid="review-status">{t('account.review.organization', { date: date(organization, i18n.language) })}</p>
        ) : null}
        <p className="text-[12px] text-faint">{t('account.review.guardrail')}</p>
      </div>
    </Card>
  );
}

function ProfileCard({ initial, onSave }: { initial: { first_name: string; last_name: string; locale: Locale }; onSave: (p: { first_name: string; last_name: string; locale: Locale }) => Promise<void> }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('saving');
    try {
      await onSave({ first_name: form.first_name.trim(), last_name: form.last_name.trim(), locale: form.locale });
      setState('saved');
    } catch {
      setState('error');
    }
  };
  return (
    <Card title={t('account.profile')}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field id="first-name" label={t('auth.firstName')} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <Field id="last-name" label={t('auth.lastName')} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </div>
        <Select id="locale" label={t('account.locale')} value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value as Locale })}>
          <option value="en">English</option>
          <option value="fr">Français</option>
        </Select>
        {state === 'saved' && <Alert kind="success">{t('app.saved')}</Alert>}
        {state === 'error' && <Alert kind="error">{t('app.error')}</Alert>}
        <div>
          <Button type="submit" disabled={state === 'saving'}>
            {state === 'saving' ? t('app.saving') : t('app.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ChangePassword() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [show, setShow] = useState(false);
  const [state, setState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }
    setState('saving');
    try {
      await api.changePassword(current, next);
      setState('done');
      setCurrent('');
      setNext('');
    } catch (err) {
      setState('idle');
      if (err instanceof ApiError && err.is('wrong_password')) setError(t('account.wrongPassword'));
      else if (err instanceof ApiError && err.is('rate_limited')) setError(t('app.rateLimited'));
      else setError(t('app.error'));
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <PasswordField id="current-password" label={t('account.currentPassword')} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} show={show} onToggle={() => setShow((v) => !v)} />
      <PasswordField id="new-password" label={t('account.newPassword')} autoComplete="new-password" hint={t('auth.passwordRule')} value={next} onChange={(e) => setNext(e.target.value)} show={show} onToggle={() => setShow((v) => !v)} />
      {state === 'done' && <Alert kind="success">{t('account.passwordChanged')}</Alert>}
      {error && <Alert kind="error">{error}</Alert>}
      <div>
        <Button type="submit" variant="secondary" disabled={state === 'saving' || !current || !next}>
          {t('account.changePassword')}
        </Button>
      </div>
    </form>
  );
}

function SetPassword() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-muted">{t('account.setPassword')}</p>
      <Link to="/forgot">
        <Button variant="secondary">{t('account.setPasswordLink')}</Button>
      </Link>
    </div>
  );
}
